import { ChangeDetectionStrategy, ChangeDetectorRef, Component, inject, OnDestroy } from '@angular/core';
import { DATA_SELECTOR_INJECTION_TOKEN, DEFAULT_LOADABLE_STATE, isEmpty, LoadableStatus } from '@aiwyn/shared';
import { BaseCommonClientRequestGroup } from '@aiwyn/common/client-requests';
import { delay, distinctUntilChanged, map, shareReplay, switchMap, takeUntil, tap } from 'rxjs/operators';
import { combineLatest, Observable, of, Subject } from 'rxjs';
import { ClientRequestsState } from '../../state';
import { Store } from '@ngxs/store';
import { ClientRequestFactoryService, ClientRequestsLayoutService } from '../../services';

@Component({
  selector: 'app-client-request-groups-container',
  templateUrl: './client-request-groups-container.component.html',
  styleUrls: ['./client-request-groups-container.component.scss', '../../styles/client-requests.scss'],
  providers: [
    {
      provide: DATA_SELECTOR_INJECTION_TOKEN,
      useValue: 'client-requests-groups-container',
    },
  ],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false,
})
export class ClientRequestGroupsContainerComponent implements OnDestroy {
  readonly LoadableStatus = LoadableStatus;
  readonly DEFAULT_LOADABLE_STATE = DEFAULT_LOADABLE_STATE;
  private readonly _unsubscribe$ = new Subject<void>();
  public dataSelector = inject(DATA_SELECTOR_INJECTION_TOKEN);

  areFiltersSelected$ = this.store.select(ClientRequestsState.getSlices.filters).pipe(
    map((filters) => !isEmpty(filters)),
    distinctUntilChanged(),
    tap((_) => this.detectChanges$.next()),
  );

  selectedGroupIdentity$ = this.store.select(ClientRequestsState.getSlices.selectedGroupIdentity);

  allFilteredClientRequestGroups$ = this.clientRequestFactoryService.getAllFilteredClientRequestGroups$(
    this.store.select(ClientRequestsState.getSlices.filters),
  );

  sortedClientRequestGroups$ = this.allFilteredClientRequestGroups$.pipe(
    switchMap(groups => {
      if (isEmpty(groups)) {
        return of([] as BaseCommonClientRequestGroup[]);
      }

      return combineLatest(groups.map(group => group.earliestDueDate$)).pipe(
        map(earliestDueDates => earliestDueDates.map((earliestDueDate, index) => ({
          index,
          earliestDueDate,
          // Convert to Date object or use max date for sorting null values
          dateObj: earliestDueDate ? new Date(earliestDueDate) : new Date(8640000000000000)
        }))),
        map(items => items.sort((a, b) => {
          // Compare using Date objects
          return a.dateObj.getTime() - b.dateObj.getTime();
        })),
        map(sortedItems => sortedItems.map(({ index }) => groups[index])),
      );
    }),
    tap(_ => this.detectChanges$.next()),
    shareReplay(1),
  );

  sortedClientRequestGroupsWithSelected$ = combineLatest([
    this.sortedClientRequestGroups$,
    this.clientRequestFactoryService.getClientRequestGroups$(this.selectedGroupIdentity$),
  ]).pipe(
    // ensure the selected group is present, so it can be displayed regardless of filter selections
    map(([sortedGroups, selectedGroup]) => sortedGroups.concat(
      selectedGroup && !sortedGroups.some(group => group.identity === selectedGroup.identity)
        ? selectedGroup
        : [],
    )),
    shareReplay(1),
  );

  showSpinner$ = combineLatest([
    this.clientRequestFactoryService.areUnidentifiedClientRequestGroupsLoading$(),
    this.clientRequestFactoryService.areIdentifiedClientRequestGroupsLoading$(),
  ]).pipe(
    map(([unidentifiedLoading, identifiedLoading]) => unidentifiedLoading && !identifiedLoading),
    distinctUntilChanged(),
    tap(_ => this.detectChanges$.next()),
  );

  applyTransitionClasses$ = this.clientRequestsLayoutService.applyTransitionClasses$;

  // Alert data for requests that need attention
  requestsNeedingAttention$ = this.allFilteredClientRequestGroups$.pipe(
    switchMap(groups => {
      if (isEmpty(groups)) {
        return of({ count: 0, projects: [] });
      }

      return combineLatest(
        groups.map(group => this.processGroupForRejections$(group))
      ).pipe(
        map(groupCounts => ({
          count: groupCounts.reduce((sum, group) => sum + group.count, 0),
          projects: groupCounts
            .filter(group => group.count > 0)
            .map(group => ({groupName: group.groupName, groupIdentity: group.groupIdentity}))
        }))
      );
    }),
    shareReplay(1)
  );

  private processGroupForRejections$(group: BaseCommonClientRequestGroup): Observable<{ count: number; groupName: string; groupIdentity: string }> {
    return combineLatest([
      group.requests$,
      group.typeClass$.pipe(switchMap(typeClass => typeClass.name$))
    ]).pipe(
      switchMap(([requests, groupName]) => {
        if (isEmpty(requests)) {
          return of({ count: 0, groupName, groupIdentity: '' });
        }

        const rejectedItemsObservables = requests.map(request => request.rejectedItems$);
        
        if (rejectedItemsObservables.length === 0) {
          return of({ count: 0, groupName, groupIdentity: '' });
        }

        return combineLatest(rejectedItemsObservables).pipe(
          map(rejectedItemsArrays => {
            // Sum up the total count of all rejected items across all requests
            const totalRejectedCount = rejectedItemsArrays
              .reduce((sum, items) => sum + (items?.length || 0), 0);
            return { count: totalRejectedCount, groupName, groupIdentity: group.identity };
          })
        );
      })
    );
  }

  detectChanges$ = new Subject<void>();
  onDetectChanges$ = this.detectChanges$.pipe(
    delay(1),
    tap(_ => this.changeDetectorRef.detectChanges()),
    takeUntil(this._unsubscribe$),
  );

  constructor(
    private readonly clientRequestFactoryService: ClientRequestFactoryService,
    public readonly clientRequestsLayoutService: ClientRequestsLayoutService,
    private readonly changeDetectorRef: ChangeDetectorRef,
    private readonly store: Store,
  ) {
    this.onDetectChanges$.subscribe();
  }

  trackClientRequestGroups(_: number, group: BaseCommonClientRequestGroup): string {
    return group.identity;
  }

  scrollToProject(project: { groupName: string; groupIdentity: string }): void {
    const groupElement = document.querySelector(`[data-testid="${this.dataSelector}-group-${project.groupIdentity}"]`);
    if (groupElement) {
      groupElement.scrollIntoView({ 
        behavior: 'smooth',
        block: 'start',
      });
    }
  }

  ngOnDestroy(): void {
    this._unsubscribe$.next();
    this._unsubscribe$.complete();
  }
}
